import { ProductFlowDataCollection } from '../product-flow/product-flow.types';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { RootReducer, Store } from '@app/app.reducers';
import { ProductFlowDesignServiceData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	getStepData,
	ProductFlowPage,
	setStepStateValid,
} from '@app/product/reducers/product-flow.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { FileUploadStatus, getUploads, UploadNotification } from '@app/shared/reducers/upload.reducer';
import { TranslatedService } from '@app/shared/services/translated.service';
import { ViewportService } from '@app/shared/services/viewport.service';
import { FormErrors, formValidationErrors$, ValidationMessages } from '@app/shared/utils/form-helper';
import { omit } from '@app/shared/utils/util';
import type { FileItem } from '@app/upload/upload.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'g-design-service',
	templateUrl: './design-service.component.html',
	styleUrls: ['./design-service.component.scss'],
})
export class DesignServiceComponent implements OnInit, ProductFlowDataCollection {
	@Input() product: api.ProductDto;
	form: UntypedFormGroup;
	notificationType = UploadNotification; // For template.
	notification: UploadNotification;
	isUploading$: Observable<boolean>;
	isUploadError$: Observable<boolean>;
	formErrors$: FormErrors;
	isFiletypeError = false;
	validationMessages: ValidationMessages;
	acceptedFileTypes: string = [
		'application/zip',
		'application/pdf',
		'application/msword',
		'application/vnd.ms-excel',
		'application/vnd.ms-powerpoint',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'application/vnd.openxmlformats-officedocument.presentationml.presentation',
		'application/postscript',
		'application/rtf',
		'application/photoshop',
		'application/illustrator',
		'application/indesign',
		'application/x-indesign',
		'text/plain',
		'text/csv',
		'image/*',
	].join(',');
	uploadedFiles: FileItem[] = [];

	constructor(
		private readonly store: Store<RootReducer.State>,
		private readonly productEditor: ProductEditorService,
		private readonly formBuilder: UntypedFormBuilder,
		private readonly translated: TranslatedService,
		public readonly viewport: ViewportService,
	) {}

	ngOnInit(): void {
		this.buildForm();

		this.store
			.select(getStepData(this.productEditor.itemId, ProductFlowPage.DesignService))
			.pipe(untilDestroyed(this))
			.subscribe((data) => {
				const stepFormData = data?.form;
				const hasForm = !!stepFormData;
				const hasCartItem = !!this.productEditor?.cartItem;
				if (hasCartItem) {
					const cartData = this.productEditor.cartItem?.productFlowData?.designService;
					if (cartData) {
						this.form.patchValue(cartData?.form);
						this.uploadedFiles = cartData?.files ?? [];
					}
				}

				if (hasForm) {
					// Step data may be more fresh than cart item data so allow overwrite
					this.form.patchValue(stepFormData);
					const stepFiles = data?.files || [];
					this.uploadedFiles = stepFiles;
				}
			});

		this.store
			.select(getUploads)
			.pipe(untilDestroyed(this))
			.subscribe((uploads) => {
				const didAllFilesSuccessfullyUpload = this.uploadedFiles.every((fileItem) => {
					const status = uploads[fileItem.key];
					return status === FileUploadStatus.Success || !status;
				});

				if (!didAllFilesSuccessfullyUpload) {
					this.notification = UploadNotification.Danger;
					this.setStateValid(false);
					return;
				}
				this.setStateValid(didAllFilesSuccessfullyUpload && this.form.status === 'VALID');
			});
	}

	collectStepData(): ProductFlowDesignServiceData {
		const data: ProductFlowDesignServiceData = {
			form: { ...(this.form.value as ProductFlowDesignServiceData['form']) },
			files: [...this.uploadedFiles.map((file) => omit(file, ['file']))],
		};
		this.productEditor.patchProductFlowData({ designService: data });
		return data;
	}

	getNotificationStyleClass(): {
		'notification-success': boolean;
		'notification-warning': boolean;
		'notification-danger': boolean;
	} {
		return {
			'notification-success': this.notification === UploadNotification.Success,
			'notification-warning': this.notification === UploadNotification.Warning,
			'notification-danger': this.notification === UploadNotification.Danger,
		};
	}

	onUploadSuccess(): void {
		this.notification = UploadNotification.Success;
	}

	onFiletypeError(): void {
		this.isFiletypeError = true;
		this.notification = UploadNotification.Danger;
	}

	onUploadStart(fileItem: FileItem): void {
		this.uploadedFiles.push(fileItem);
		this.isFiletypeError = false;
		this.notification = UploadNotification.Warning;
	}

	resetUploadSettings(): void {
		this.notification = undefined;
	}

	private setStateValid(isValid: boolean): void {
		this.store.dispatch(setStepStateValid(isValid));
	}

	private buildForm(): void {
		this.validationMessages = {
			description: {
				required: this.translated.string('productDesignService.descriptionRequired'),
			},
			email: {
				required: this.translated.string('productDesignService.emailRequired'),
				email: this.translated.string('productDesignService.emailFormat'),
				maxlength: this.translated.string('productDesignService.emailMaxLength'),
			},
		};

		this.form = this.formBuilder.group(
			{
				description: ['', [Validators.required]],
				email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
			},
			{
				updateOn: 'change',
			},
		);

		this.formErrors$ = formValidationErrors$(this.form, this.validationMessages);

		this.form.statusChanges.pipe(debounceTime(500), untilDestroyed(this)).subscribe((formStatus) => {
			this.setStateValid(formStatus === 'VALID');
		});
	}
}
